/**
 * Pure pothole-site planning. Gameplay owns spawning and presentation; this module only
 * chooses existing, legal world candidates. It deliberately has no THREE dependency so the
 * layout can be exercised over thousands of seeds in a fast Node regression.
 */

// Fallbacks for callers that omit a rule. Production always passes TUNING.layout; keep these
// in step with it so an isolated test never silently exercises retired spacing semantics.
const DEFAULTS = Object.freeze({
  hardMinSpacing: 36,
  preferredSpacing: 60,
  activeRadius: 170,
  maxLink: 120,
  spacingFloor: 0.06,
  newBandWeight: 1.6,
  axisBalanceExponent: 0.75,
  bandRepeatDecay: 0.25,
  fallbackRadiusStep: 15,
  fallbackLinkStep: 15,
  fallbackTiers: 6,
  openingSearchRadius: 34,
  openingMinForward: 0,
  openingMinDistance: 12,
  openingMaxDistance: 40,
});

const clamp01 = (v) => Math.max(0, Math.min(1, v));

function smoothstep(a, b, value) {
  if (!(b > a)) return value >= b ? 1 : 0;
  const t = clamp01((value - a) / (b - a));
  return t * t * (3 - 2 * t);
}

function value(rules, key) {
  const v = rules && rules[key];
  return Number.isFinite(v) ? v : DEFAULTS[key];
}

function point(site) {
  if (!site) return null;
  const p = site.pos || site.position || site;
  return p && Number.isFinite(p.x) && Number.isFinite(p.z) ? p : null;
}

function axisOf(site) {
  if (site && (site.axis === 0 || site.axis === 1)) return site.axis;
  const d = site && site.dir;
  if (d) {
    const dx = Number.isFinite(d.x) ? Math.abs(d.x) : 0;
    const dz = Number.isFinite(d.z) ? Math.abs(d.z) :
      (Number.isFinite(d.y) ? Math.abs(d.y) : 0);
    if (dx || dz) return dx > dz ? 1 : 0;
  }
  return -1;
}

function bandOf(site) {
  if (site && site.band != null) return String(site.band);
  const axis = axisOf(site);
  const index = site && (site.streetIndex ?? site.lineIndex);
  if (axis >= 0 && Number.isFinite(index)) return `${axis}:${index}`;

  // Metadata is expected on production candidates. This spatial fallback keeps isolated
  // tests and fallback worlds useful without pretending perpendicular streets are one band.
  const p = point(site);
  if (!p || axis < 0) return 'unknown';
  const lateral = axis === 0 ? p.x : p.z;
  return `${axis}:~${Math.round(lateral / 16)}`;
}

function dist2(a, b) {
  const dx = a.x - b.x, dz = a.z - b.z;
  return dx * dx + dz * dz;
}

function spatialCompare(a, b) {
  const pa = point(a), pb = point(b);
  if (!pa || !pb) return pa ? -1 : pb ? 1 : 0;
  return pa.x - pb.x || pa.z - pb.z || axisOf(a) - axisOf(b) ||
    bandOf(a).localeCompare(bandOf(b));
}

/**
 * Return the legal world candidate nearest the desired opening point, or null. Candidates
 * behind or unreasonably close/far from the player are not considered. The desired-point
 * radius is preferred; the nearest otherwise-legal candidate is the deterministic fallback.
 * Source ordering is not a tie-breaker.
 */
export function selectOpeningSite(candidates, desired, origin, forward, rules) {
  const dp = point(desired), op = point(origin);
  if (!Array.isArray(candidates) || !dp || !op || !forward) return null;

  const fx0 = Number.isFinite(forward.x) ? forward.x : 0;
  const fz0 = Number.isFinite(forward.z) ? forward.z :
    (Number.isFinite(forward.y) ? forward.y : 0);
  const fl = Math.hypot(fx0, fz0);
  if (fl < 1e-9) return null;
  const fx = fx0 / fl, fz = fz0 / fl;
  const maxD2 = value(rules, 'openingSearchRadius') ** 2;
  const minForward = value(rules, 'openingMinForward');
  const minPlayerD2 = value(rules, 'openingMinDistance') ** 2;
  const maxPlayerD2 = value(rules, 'openingMaxDistance') ** 2;

  let best = null, bestD2 = Infinity;
  let fallback = null, fallbackD2 = Infinity;
  for (const candidate of candidates) {
    const p = point(candidate);
    if (!p) continue;
    const playerD2 = dist2(p, op);
    if (playerD2 < minPlayerD2 || playerD2 > maxPlayerD2) continue;
    const ahead = (p.x - op.x) * fx + (p.z - op.z) * fz;
    if (ahead < minForward) continue;
    const d2 = dist2(p, dp);
    if (d2 < fallbackD2 || (d2 === fallbackD2 && spatialCompare(candidate, fallback) < 0)) {
      fallback = candidate;
      fallbackD2 = d2;
    }
    if (d2 > maxD2) continue;
    if (d2 < bestD2 || (d2 === bestD2 && spatialCompare(candidate, best) < 0)) {
      best = candidate;
      bestD2 = d2;
    }
  }
  return best || fallback;
}

function fallbackLimits(tier, rules) {
  // Expand the active district first, then its connectivity limit, in alternating steps.
  // A final hard-only tier guarantees graceful degradation without ever weakening spacing.
  const finiteTiers = Math.max(0, Math.floor(value(rules, 'fallbackTiers')));
  if (tier > finiteTiers) return { radius: Infinity, link: Infinity };
  return {
    radius: value(rules, 'activeRadius') +
      Math.ceil(tier * 0.5) * value(rules, 'fallbackRadiusStep'),
    link: value(rules, 'maxLink') +
      Math.floor(tier * 0.5) * value(rules, 'fallbackLinkStep'),
  };
}

/**
 * Select `count` additional candidates around `opening`.
 *
 * The opening is used for distance, axis and band accounting but is not returned. Inputs are
 * never mutated and the result contains the original candidate objects. The RNG must expose
 * `pickWeighted(items, weights)`; the repository's Rng does so deterministically.
 */
export function selectPotholeSites(candidates, opening, count, rng, rules) {
  const openingPoint = point(opening);
  const wanted = Math.max(0, Math.floor(count));
  if (!Array.isArray(candidates) || !openingPoint || !rng ||
      typeof rng.pickWeighted !== 'function' || wanted === 0) return [];

  const hard = value(rules, 'hardMinSpacing');
  const preferred = Math.max(hard, value(rules, 'preferredSpacing'));
  const floor = clamp01(value(rules, 'spacingFloor'));
  const axisExponent = value(rules, 'axisBalanceExponent');
  const newBandWeight = Math.max(0, value(rules, 'newBandWeight'));
  const bandDecay = Math.max(0, value(rules, 'bandRepeatDecay'));
  const hard2 = hard * hard;

  // A canonical spatial order ensures reordering the world's source array cannot change the
  // layout. Invalid entries are discarded; duplicate positions are rejected by hard spacing.
  const pool = candidates.filter(point).slice().sort(spatialCompare);
  const selected = [opening];
  const additional = [];
  const finiteTiers = Math.max(0, Math.floor(value(rules, 'fallbackTiers')));

  while (additional.length < wanted) {
    const axisCounts = [0, 0];
    const bandCounts = new Map();
    for (const site of selected) {
      const axis = axisOf(site);
      if (axis >= 0) axisCounts[axis]++;
      const band = bandOf(site);
      bandCounts.set(band, (bandCounts.get(band) || 0) + 1);
    }

    let eligible = null, nearest = null;
    for (let tier = 0; tier <= finiteTiers + 1; tier++) {
      const limits = fallbackLimits(tier, rules);
      const radius2 = limits.radius * limits.radius;
      const link2 = limits.link * limits.link;
      const e = [], ds = [];

      for (const candidate of pool) {
        if (selected.includes(candidate)) continue;
        const p = point(candidate);
        if (dist2(p, openingPoint) > radius2) continue;

        let nearest2 = Infinity;
        for (const site of selected) nearest2 = Math.min(nearest2, dist2(p, point(site)));
        if (nearest2 < hard2 || nearest2 > link2) continue;
        e.push(candidate);
        ds.push(Math.sqrt(nearest2));
      }
      if (e.length) { eligible = e; nearest = ds; break; }
    }

    if (!eligible) break; // The candidate pool cannot satisfy the hard minimum.

    const weights = new Array(eligible.length);
    for (let i = 0; i < eligible.length; i++) {
      const candidate = eligible[i];
      const spacing = floor + (1 - floor) * smoothstep(hard, preferred, nearest[i]);
      const axis = axisOf(candidate);
      const axisCount = axis >= 0 ? axisCounts[axis] : selected.length * 0.5;
      const axisBalance = Math.pow(
        (selected.length + 2) / (2 * (axisCount + 1)), axisExponent
      );
      const repeats = bandCounts.get(bandOf(candidate)) || 0;
      const streetBalance = repeats === 0 ? newBandWeight : 1 / (1 + bandDecay * repeats);
      weights[i] = spacing * axisBalance * streetBalance;
    }

    const chosen = rng.pickWeighted(eligible, weights);
    if (!chosen) break;
    selected.push(chosen);
    additional.push(chosen);
  }
  return additional;
}
